import { TableEmptyContent } from '@avanta/shared/components/TableEmptyContent';
import { Card } from '@avanta/shared/components/ui/Card';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { EMPTY_VALUE } from 'constants/common';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { getSickLeaveStartDate } from 'pages/PatientProfile/utils';
import { ActivePatientRecordSkeleton } from './ActivePatientRecordSkeleton';

export const ActivePatientRecord = (): React.ReactElement => {
  const params = useParams();
  const patientId = params?.id ?? '';
  const { data: patientRecord, isLoading } = useFetchActivePatientRecord(patientId);
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  const renderContent = (): React.ReactElement => {
    if (isLoading) {
      return <ActivePatientRecordSkeleton />;
    }

    if (patientRecord) {
      return (
        <div className="grid grid-cols-3 gap-6">
          <Input
            type="text"
            className="w-full"
            label={t('PatientRecords.fields.code')}
            value={patientRecord.code || EMPTY_VALUE}
            readOnly
            variant="filled"
          />
          <Input
            type="text"
            className="w-full"
            label={t('PatientRecords.fields.contract')}
            value={patientRecord.contract?.code || EMPTY_VALUE}
            readOnly
            variant="filled"
          />
          <Input
            type="text"
            className="w-full"
            label={t('PatientRecords.fields.sickLeaveStart')}
            value={getSickLeaveStartDate(patientRecord)}
            readOnly
            variant="filled"
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-2 text-center h-full">
        <TableEmptyContent
          emptyTitle={t('PatientRecords.active.empty.title')}
          emptyMessage={t('PatientRecords.active.empty.description')}
        />
      </div>
    );
  };

  return (
    <Card
      variant="primary"
      title={t('PatientRecords.active.heading')}
      icon={FolderOpen}
      className={cn(
        'am-md:px-8 am-md:pb-6 am-md:pt-8 justify-center',
        patientRecord ? 'h-31' : 'h-38'
      )}
      aria-busy={isLoading}
    >
      {renderContent()}
    </Card>
  );
};
